import ROUTES from '../../constants/Routes';

const EXTENSION_TO_FILE_TYPE = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  txt: 'text/plain',
  hl7: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  docm: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsm: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlsb: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  bmp: 'image/bmp',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
};

const MIME_TYPE_ALIASES = {
  'application/CDFV2': 'application/msword',
};

const OFFICE_FILE_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

const AMBIGUOUS_FILE_TYPES = new Set([
  'application/octet-stream',
  'application/zip',
]);

const getFileExtension = (fileName = '') => {
  const normalizedName = String(fileName).split(/[?#]/)[0];
  const parts = normalizedName.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

export const normalizeDocumentFileType = (mimeType, fileName) => {
  const extension = getFileExtension(fileName);
  const normalizedMimeType = String(mimeType || '').split(';')[0].trim();
  return EXTENSION_TO_FILE_TYPE[extension]
    || MIME_TYPE_ALIASES[normalizedMimeType]
    || normalizedMimeType
    || extension;
};

export const canPreviewDocument = (fileType, renderers = []) => (
  Boolean(fileType)
  && !AMBIGUOUS_FILE_TYPES.has(fileType)
  && renderers.some((renderer) => renderer.fileTypes?.includes(fileType))
);

export const usesOnlineOfficePreview = (fileType) => OFFICE_FILE_TYPES.has(fileType);

export const getDocumentViewerPath = ({ chartId, batchId }) => {
  if (chartId) {
    return ROUTES.DOCUMENT_VIEWER_CHART.replace(':chartId', encodeURIComponent(chartId));
  }

  if (batchId) {
    return ROUTES.DOCUMENT_VIEWER_BATCH.replace(':batchId', encodeURIComponent(batchId));
  }

  return null;
};

export const openDocumentViewer = (viewerPath) => {
  if (!viewerPath) return false;

  const viewerWindow = window.open(
    viewerPath,
    '_blank',
    'popup=yes,width=1440,height=900,resizable=yes,scrollbars=yes'
  );

  if (!viewerWindow) return false;

  // The viewer is a self-contained same-origin application page and does not
  // need access to the window that launched it.
  viewerWindow.opener = null;
  viewerWindow.focus();
  return true;
};

const getSafeDownloadName = (fileName) => {
  const name = String(fileName || 'document').split(/[\\/]/).pop().trim();
  return name || 'document';
};

const getSafeChartId = (chartId) => String(chartId || '')
  .replace(/[<>:"/\\|?*]/g, '-')
  .replace(/[. ]+$/g, '')
  .trim();

export const buildDocumentDownloadName = (fileName, chartId) => {
  const safeFileName = getSafeDownloadName(fileName);
  const safeChartId = getSafeChartId(chartId);

  if (!safeChartId) return safeFileName;

  const normalizedFileName = safeFileName.toLowerCase();
  const normalizedChartId = safeChartId.toLowerCase();
  const alreadyPrefixed = normalizedFileName === normalizedChartId
    || normalizedFileName.startsWith(`${normalizedChartId}_`)
    || normalizedFileName.startsWith(`${normalizedChartId}-`)
    || normalizedFileName.startsWith(`${normalizedChartId}.`);

  return alreadyPrefixed ? safeFileName : `${safeChartId}_${safeFileName}`;
};

const triggerBrowserDownload = (url, fileName, openInNewTab = false) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = getSafeDownloadName(fileName);
  if (openInNewTab) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadDocument = async ({ uri, fileName }) => {
  if (!uri) return;

  try {
    const response = await fetch(uri);
    if (!response.ok) throw new Error('Unable to download document');

    const blobUrl = URL.createObjectURL(await response.blob());
    triggerBrowserDownload(blobUrl, fileName);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    // Signed storage URLs can reject cross-origin fetches even when direct
    // navigation is allowed. The anchor fallback lets storage headers handle
    // the download in that case.
    triggerBrowserDownload(uri, fileName, true);
  }
};
